import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { GrocyCacheApi } from '../../../../../core/api/grocy/cache.api';
import { GrocyContextDataApi } from '../../../../../core/api/grocy/context-data.api';
import { GrocyProductsApi } from '../../../../../core/api/grocy/products.api';
import { GrocyProductBasics } from '../../../../../core/entities/grocy/products/product-basics.entity';
import { GrocyDashboardProductForm } from '../../../dashboard/forms/dashboard-product.form';
import { GrocyDashboardService } from '../../services/dashboard.service';

@Component({
  selector: 'app-grocy-product-select',
  templateUrl: './product-select.component.html',
  styleUrl: './product-select.component.scss',
})
export class ProductSelectComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('barcodeInput', { read: ElementRef<HTMLInputElement> }) barcodeInput!: ElementRef<HTMLInputElement>;

  @Input() hideNotInStock = true;

  productChangeListener?: Subscription;
  subProductOptions: GrocyProductBasics[] = [];

  constructor(
    private cacheApi: GrocyCacheApi,
    private contextDataApi: GrocyContextDataApi,
    private productsApi: GrocyProductsApi,
    private service: GrocyDashboardService
  ) {}

  ngOnInit(): void {
    this.loadSubProductOptions();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.barcodeInput.nativeElement.focus();
      this.barcodeInput.nativeElement.select();
    }, 100);
  }

  ngOnDestroy(): void {
    this.productChangeListener?.unsubscribe();
  }

  get currentStock(): string {
    return this.service.selectedProductStockFormatted();
  }

  get form(): GrocyDashboardProductForm {
    return this.service.forms.product;
  }

  get isLoading(): boolean {
    return this.service.state.isLoading;
  }

  get productSelected(): boolean {
    return this.service.productSelected();
  }

  clearProduct(): void {
    this.service.clearSelectedProduct();

    setTimeout(() => {
      this.barcodeInput.nativeElement.click();
      this.barcodeInput.nativeElement.focus();
    }, 100);
  }

  openProductPage(event: PointerEvent): void {
    const selectedProduct = this.service.selectedProduct();

    if (selectedProduct && event.pointerId === 1) {
      let productId = selectedProduct.id;

      if (selectedProduct.parentProductId) {
        productId = selectedProduct.parentProductId;
      }

      window.open(`grocy/products/${productId}`, '_blank');
    }
  }

  refreshProductOptions(): void {
    this.cacheApi.refresh().subscribe(() => {
      this.contextDataApi.refresh();
      this.loadSubProductOptions();
    });
  }

  private setBarcodeChangeListener(): void {
    this.service.forms.product.controls.barcode.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(barcode => {
        if (this.service.state.isLoading) return;

        if (barcode) {
          this.service.findBarcode(barcode);
        } else this.service.clearSelectedProduct();
      });
  }

  private setProductChangeListener(): void {
    this.productChangeListener = this.form.controls.product.valueChanges.pipe(debounceTime(300)).subscribe(name => {
      if (this.service.state.isLoading || name === this.service.selectedProductName()) return;

      if (!name) {
        this.form.options.grocyProducts = this.subProductOptions;

        this.service.forms.shopping.item.clear();

        if (this.service.forms.product.barcodeExists) {
          this.form.barcodeExists = false;
          this.form.controls.barcode.setValue('');
        }
      } else {
        this.form.options.grocyProducts = this.subProductOptions.filter(p =>
          p.name.toLowerCase().includes(name.toLowerCase())
        );

        const exactMatches = this.form.options.grocyProducts.filter(p => p.name.toLowerCase() === name.toLowerCase());

        if (exactMatches.length === 1) {
          const product = exactMatches[0];
          this.service.findDetailedProductByProductId(product.id);
        }
      }
    });
  }

  private setSubmissionListener(): void {
    this.service.submission.subscribe(() => {
      this.barcodeInput.nativeElement.focus();
      this.barcodeInput.nativeElement.select();
    });
  }

  private loadSubProductOptions(): void {
    this.productsApi.findSubProductOptions().subscribe(options => {
      this.subProductOptions = options;
      this.form.options.grocyProducts = options;
      this.setBarcodeChangeListener();
      this.setProductChangeListener();
      this.setSubmissionListener();
    });
  }
}
